import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import Select from "react-select";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import Icon from "../../common/elements/Icon";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { useThor } from "../context/ThorContext";
import { PROJECT_MAP } from "../utils/thorProjects";
import { serverErrMsg } from "../utils/validators";
import Button from "../../common/elements/Button";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";

const TYPE_OPTIONS = [
    { value: 'a/b', label: 'A/B Test' },
    { value: 'multivariate', label: 'Multivariate' },
    { value: 'multiarmed_bandit', label: 'Multiarmed Bandit' },
    { value: 'a/a', label: 'A/A Test' },
];

const PROJECT_OPTIONS = Object.entries(PROJECT_MAP)
    .map(([id, name]) => ({ value: Number(id), label: name }))
    .sort((a, b) => {
        if (a.label.startsWith('DO NOT USE')) return 1;
        if (b.label.startsWith('DO NOT USE')) return -1;
        return a.label.localeCompare(b.label);
    });

const sp = (hasError) => ({
    menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
    menuPosition: 'fixed',
    styles: {
        menuPortal: base => ({ ...base, zIndex: 9999 }),
        control: base => ({ ...base, borderColor: hasError ? '#f14668' : base.borderColor }),
    },
});

// Required field validator map
const REQUIRED = {
    name: 'Experiment Name',
    description: 'Description',
    email: 'Owner Email',
    jiraTicket: 'Jira Ticket',
    specUrl: 'Spec URL',
    productManager: 'Product Manager',
};

const validate = (form) => {
    const errors = {};
    Object.entries(REQUIRED).forEach(([key, label]) => {
        if (!form[key]?.trim()) errors[key] = `${label} is required.`;
    });
    if (form.projectId == null) errors.projectId = 'Project is required.';
    return errors;
};

const Field = ({ label, required, error, children }) => (
    <div className="field">
        <label className="label is-small">
            {label}{required && <span className="has-text-danger ml-1">*</span>}
        </label>
        <div className="control">{children}</div>
        {error && <p className="help is-danger">{error}</p>}
    </div>
);

const BASE_FORM = {
    name: '', description: '', type: 'a/b', projectId: null,
    email: '', jiraTicket: '', specUrl: '', productManager: '',
    trafficAllocation: 0, isMemberTest: false, isLongRunning: false,
    isAnalysisRequired: true, userReshuffleType: null,
    hypothesisIf: '', hypothesisThen: '', hypothesisBecause: '',
};

// Fields carried over from the source experiment when cloning.
// The backend additionally deep-copies buckets, variables, metrics,
// audiences, segments and stakeholders.
const seedFromDetail = (detail) => ({
    ...BASE_FORM,
    name: detail.name ? `Copy of ${detail.name}` : '',
    // description intentionally left blank — written fresh for each clone.
    type: detail.type || 'a/b',
    projectId: detail.projectId ?? null,
    email: detail.email ?? '',
    productManager: detail.productManager ?? detail.owner ?? '',
    isMemberTest: detail.memberTest ?? detail.isMemberTest ?? false,
    isLongRunning: detail.longRunning ?? detail.isLongRunning ?? false,
    isAnalysisRequired: detail.analysisRequired ?? detail.isAnalysisRequired ?? true,
    trafficAllocation: detail.trafficAllocation ?? 0,
    userReshuffleType: detail.userReshuffleType ?? null, // "Salt" in Retool
    hypothesisIf: detail.hypothesisIf ?? '',
    hypothesisThen: detail.hypothesisThen ?? '',
    hypothesisBecause: detail.hypothesisBecause ?? '',
    // jiraTicket + specUrl are intentionally left blank — they are
    // per-experiment and must be re-entered for the clone.
});

// Read-only preview of everything the backend deep-copies from the source.
// Each `extract` mirrors the response shape parsed by the corresponding tab
// (BucketsTab, VariablesTab, MetricsTab, AudiencesTab, SegmentsTab, DetailsTab)
// and returns a flat list of display strings.
const arr = (v) => (Array.isArray(v) ? v : []);
const COPIED_SECTIONS = [
    { key: 'buckets', label: 'Buckets', url: thor_api_urls.experimentBuckets,
      extract: raw => arr(raw?.buckets ?? raw).map(b => b.name ?? b.key) },
    { key: 'variables', label: 'Variables', url: thor_api_urls.experimentVariables,
      extract: raw => arr(raw?.variables).map(v => v.key) },
    { key: 'metrics', label: 'Metrics', url: thor_api_urls.experimentMetrics,
      extract: raw => arr(raw?.metrics ?? raw).map(m => m.label ?? m.eventKey ?? m.key) },
    { key: 'audiences', label: 'Audiences', url: thor_api_urls.experimentAudiences,
      extract: raw => arr(raw?.experimentAudiences ?? raw).map(a => a.audienceName ?? a.attributeName) },
    { key: 'segments', label: 'Segments', url: thor_api_urls.experimentSegments,
      extract: raw => arr(raw?.segments).map(s => s.segmentName ?? s.name) },
    // NOTE: stakeholders are NOT here — they are seeded into an editable
    // section of the form (see STAKEHOLDER_ROLES) so the user can adjust them.
];

const STAKEHOLDER_ROLES = { ANALYST: 'Analyst', PRODUCT: 'Product Manager', ENGINEER: 'Engineer' };
const splitEmails = (s) => (s ? s.split(',').map(x => x.trim()).filter(Boolean) : []);

const CloneSummary = ({ sourceKey, execute }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch all copied sections in parallel whenever the source changes.
    useEffect(() => {
        let active = true;
        setLoading(true);
        setData(null);
        Promise.all(COPIED_SECTIONS.map(s =>
            execute('GET', s.url(sourceKey))
                .then(raw => s.extract(raw).filter(Boolean).map(String))
                .catch(() => null) // null = couldn't load (distinct from empty)
        )).then(results => {
            if (!active) return;
            const next = {};
            COPIED_SECTIONS.forEach((s, i) => { next[s.key] = results[i]; });
            setData(next);
            setLoading(false);
        });
        return () => { active = false; };
    }, [sourceKey, execute]);

    if (loading) {
        return <p className="is-size-7 has-text-grey mt-3"><Icon icon={faCircleInfo} /> Loading what will be copied…</p>;
    }

    return (
        <div className="mt-3">
            <p className="is-size-7 has-text-weight-semibold mb-2">
                <Icon icon={faCircleInfo} /> Copied as-is from the source (read-only)
            </p>
            <div className="columns is-multiline is-mobile">
                {COPIED_SECTIONS.map(s => {
                    const items = data?.[s.key];
                    const count = items?.length ?? 0;
                    return (
                        <div className="column is-half" key={s.key}>
                            <p className="is-size-7 has-text-weight-semibold">
                                {s.label}{' '}
                                <span className="tag is-light is-rounded ml-1">{items == null ? '–' : count}</span>
                            </p>
                            {items == null ? (
                                <p className="is-size-7 has-text-grey-light">Couldn’t load</p>
                            ) : count === 0 ? (
                                <p className="is-size-7 has-text-grey-light">None</p>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                    {items.slice(0, 8).map((n, i) => (
                                        <span key={i} className="tag is-white is-small"
                                              style={{ border: '1px solid #e6e8eb' }}>{String(n)}</span>
                                    ))}
                                    {count > 8 && <span className="is-size-7 has-text-grey">+{count - 8} more</span>}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const CreateExperimentForm = ({ onCreated, onCancel, mode = 'blank', experiments = [] }) => {
    const { execute } = useFetchWithMsal();
    const { instance } = useMsal();
    const { addToast } = useThor();
    const isClone = mode === 'clone';
    const userEmail = instance.getAllAccounts()[0]?.username ?? '';
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({ ...BASE_FORM });
    const [sourceKey, setSourceKey] = useState(null);
    const [seeding, setSeeding] = useState(false);
    const [mineOnly, setMineOnly] = useState(false);
    const [stakeholders, setStakeholders] = useState({ ANALYST: '', PRODUCT: '', ENGINEER: '' });

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const isMine = (e) =>
        e.owner?.toLowerCase() === userEmail.toLowerCase() ||
        e.email?.toLowerCase() === userEmail.toLowerCase();

    const sourceOptions = experiments
        .filter(e => !mineOnly || (userEmail && isMine(e)))
        .map(e => ({ value: e.key, label: `${e.name ?? e.key} — ${e.key}` }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const handleSourceChange = (opt) => {
        const key = opt?.value ?? null;
        setSourceKey(key);
        if (!key) {
            setForm({ ...BASE_FORM });
            setStakeholders({ ANALYST: '', PRODUCT: '', ENGINEER: '' });
            return;
        }
        setSeeding(true);
        Promise.all([
            execute('GET', thor_api_urls.experiment(key)),
            execute('GET', thor_api_urls.experimentStakeholders(key)).catch(() => null),
        ])
            .then(([raw, sRaw]) => {
                const detail = (raw?.experiment && typeof raw.experiment === 'object') ? raw.experiment : raw;
                setForm(seedFromDetail(detail));
                const sMap = sRaw?.stakeholders ?? {};
                setStakeholders({
                    ANALYST: (sMap.ANALYST || []).join(', '),
                    PRODUCT: (sMap.PRODUCT || []).join(', '),
                    ENGINEER: (sMap.ENGINEER || []).join(', '),
                });
            })
            .catch(err => {
                console.error("Failed to load source experiment:", err);
                addToast(serverErrMsg(err, 'Failed to load the source experiment.'), 'danger');
            })
            .finally(() => setSeeding(false));
    };

    const errors = submitted ? validate(form) : {};
    const hasErrors = Object.keys(validate(form)).length > 0;
    const noSource = isClone && !sourceKey;

    const handleSubmit = () => {
        setSubmitted(true);
        if (hasErrors || noSource) return;
        setIsSubmitting(true);
        const url = isClone ? thor_api_urls.cloneExperiment(sourceKey) : thor_api_urls.createExperiment;
        execute('POST', url, form)
            .then(data => {
                const exp = data?.experiment ?? data;
                const newKey = exp.key ?? exp.summary?.key;
                // On clone, persist the (possibly edited) stakeholders onto the
                // new experiment, overwriting the ones the backend auto-copied.
                if (!isClone || !newKey) return exp;
                const stakeholderPayload = {
                    stakeholders: {
                        ANALYST: splitEmails(stakeholders.ANALYST),
                        PRODUCT: splitEmails(stakeholders.PRODUCT),
                        ENGINEER: splitEmails(stakeholders.ENGINEER),
                    },
                };
                return execute('POST', thor_api_urls.experimentStakeholders(newKey), stakeholderPayload)
                    .catch(err => {
                        console.error("Clone succeeded but saving stakeholders failed:", err);
                        addToast('Experiment copied, but updating stakeholders failed — adjust them on the Stakeholders tab.', 'warning');
                    })
                    .then(() => exp);
            })
            .then(exp => {
                addToast(`Experiment "${exp.name ?? form.name}" ${isClone ? 'copied' : 'created'}.`, 'success');
                onCreated(exp);
            })
            .catch(err => {
                console.error(`Failed to ${isClone ? 'copy' : 'create'} experiment:`, err);
                addToast(serverErrMsg(err, `Failed to ${isClone ? 'copy' : 'create'} experiment.`), 'danger');
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <div>
            <div className="level mb-4">
                <div className="level-left">
                    <p className="title is-5 mb-0">{isClone ? 'Create from Existing Experiment' : 'Create Experiment'}</p>
                </div>
                <div className="level-right" style={{ gap: '0.5rem', display: 'flex' }}>
                    <Button className="is-link is-small" onClick={handleSubmit} disabled={isSubmitting || seeding}>
                        {isSubmitting ? (isClone ? 'Copying…' : 'Creating…') : (isClone ? 'Create Copy' : 'Create')}
                    </Button>
                    <Button className="is-light is-small" onClick={onCancel}>Cancel</Button>
                </div>
            </div>

            {submitted && noSource && (
                <div className="notification is-danger is-light mb-4">
                    Pick a source experiment to clone from.
                </div>
            )}
            {submitted && hasErrors && (
                <div className="notification is-danger is-light mb-4">
                    Please fill in all required fields before creating.
                </div>
            )}

            {isClone && (
                <div className="box mb-4" style={{ background: '#fafbfc' }}>
                    <div className="field">
                        <div className="is-flex is-justify-content-space-between is-align-items-center">
                            <label className="label is-small mb-0">
                                Source experiment<span className="has-text-danger ml-1">*</span>
                            </label>
                            <label className="checkbox is-size-7 has-text-grey" title="Show only experiments you own">
                                <input type="checkbox" className="mr-1"
                                       checked={mineOnly}
                                       onChange={e => setMineOnly(e.target.checked)}
                                       disabled={!userEmail} />
                                Mine only
                            </label>
                        </div>
                        <div className="control mt-1">
                            <Select options={sourceOptions}
                                    value={sourceOptions.find(o => o.value === sourceKey) ?? null}
                                    onChange={handleSourceChange}
                                    isLoading={seeding}
                                    noOptionsMessage={() => mineOnly ? 'No experiments owned by you' : 'No experiments'}
                                    placeholder="Search experiments to copy…"
                                    {...sp(submitted && noSource)} />
                        </div>
                        {submitted && noSource && <p className="help is-danger">Select an experiment to copy.</p>}
                    </div>
                    {sourceKey && <CloneSummary sourceKey={sourceKey} execute={execute} />}
                </div>
            )}

            <p className="has-text-grey is-size-7 mb-4">
                {isClone
                    ? 'The fields below are pre-filled from the source and stay editable (Description, Jira & Spec are cleared on purpose). Buckets, variables (incl. per-bucket values), metrics, audiences and segments are copied as-is (shown above, read-only). Stakeholders are copied into an editable section below. State, approvals, schedule, instrumentation and outcome start fresh.'
                    : 'Fill in the required fields to create the experiment. All other details (hypothesis, instrumentation, metrics, segments…) can be edited afterwards from the sidebar.'}
            </p>

            <p className="has-text-weight-semibold is-size-6 mb-3">Basic Info</p>
            <div className="columns is-multiline">
                <div className="column is-half">
                    <Field label="Experiment Name" required error={errors.name}>
                        <input className={`input is-small ${errors.name ? 'is-danger' : ''}`}
                               type="text" value={form.name}
                               onChange={e => set('name', e.target.value)}
                               placeholder="e.g. Homepage hero test" />
                    </Field>
                </div>
                <div className="column is-half">
                    <Field label="Project" required error={errors.projectId}>
                        <Select options={PROJECT_OPTIONS}
                                value={PROJECT_OPTIONS.find(o => o.value === form.projectId) ?? null}
                                onChange={o => set('projectId', o?.value ?? null)}
                                placeholder="Select project…"
                                {...sp(!!errors.projectId)} />
                    </Field>
                </div>
                <div className="column is-half">
                    <Field label={<>Experiment Type<HelpTooltip text={HELP.experimentType} position="bottom" /></>}>
                        <Select options={TYPE_OPTIONS}
                                value={TYPE_OPTIONS.find(o => o.value === form.type) ?? null}
                                onChange={o => set('type', o?.value ?? 'a/b')}
                                {...sp(false)} />
                    </Field>
                </div>
                <div className="column is-half">
                    <Field label={<>Traffic Allocation (%)<HelpTooltip text={HELP.config.trafficAllocation} /></>}>
                        <input className="input is-small" type="number" min={0} max={100}
                               value={form.trafficAllocation}
                               onChange={e => set('trafficAllocation', Number(e.target.value))} />
                    </Field>
                </div>
                <div className="column is-half">
                    <Field label="Owner Email" required error={errors.email}>
                        <input className={`input is-small ${errors.email ? 'is-danger' : ''}`}
                               type="email" value={form.email}
                               onChange={e => set('email', e.target.value)}
                               placeholder="you@tripadvisor.com" />
                    </Field>
                </div>
                <div className="column is-half">
                    <Field label="Product Manager" required error={errors.productManager}>
                        <input className={`input is-small ${errors.productManager ? 'is-danger' : ''}`}
                               type="text" value={form.productManager}
                               onChange={e => set('productManager', e.target.value)}
                               placeholder="e.g. Jane Smith" />
                    </Field>
                </div>
                <div className="column is-half">
                    <Field label="Jira Ticket" required error={errors.jiraTicket}>
                        <input className={`input is-small ${errors.jiraTicket ? 'is-danger' : ''}`}
                               type="text" value={form.jiraTicket}
                               onChange={e => set('jiraTicket', e.target.value)}
                               placeholder="e.g. EXP-123" />
                    </Field>
                </div>
                <div className="column is-half">
                    <Field label="Spec URL" required error={errors.specUrl}>
                        <input className={`input is-small ${errors.specUrl ? 'is-danger' : ''}`}
                               type="text" value={form.specUrl}
                               onChange={e => set('specUrl', e.target.value)}
                               placeholder="https://…" />
                    </Field>
                </div>
                <div className="column is-full">
                    <Field label="Description" required error={errors.description}>
                        <textarea className={`textarea is-small ${errors.description ? 'is-danger' : ''}`}
                                  rows={2} value={form.description}
                                  onChange={e => set('description', e.target.value)}
                                  placeholder="Brief description of what this experiment tests…" />
                    </Field>
                </div>
            </div>

            {isClone && (
                <>
                    <p className="has-text-weight-semibold is-size-6 mb-3 mt-2">
                        Stakeholders <span className="has-text-grey is-size-7 has-text-weight-normal">(copied from source — editable)</span>
                    </p>
                    <div className="columns is-multiline">
                        {Object.entries(STAKEHOLDER_ROLES).map(([role, label]) => (
                            <div className="column is-one-third" key={role}>
                                <Field label={label}>
                                    <input className="input is-small" type="text"
                                           value={stakeholders[role] ?? ''}
                                           placeholder="Comma-separated emails"
                                           onChange={e => setStakeholders(s => ({ ...s, [role]: e.target.value }))} />
                                </Field>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <p className="has-text-weight-semibold is-size-6 mb-3 mt-2">Hypothesis <span className="has-text-grey is-size-7 has-text-weight-normal">(optional)</span></p>
            <div className="columns is-multiline">
                <div className="column is-full">
                    <Field label="If…">
                        <textarea className="textarea is-small" rows={2} value={form.hypothesisIf}
                                  onChange={e => set('hypothesisIf', e.target.value)}
                                  placeholder="e.g. If we show users a featured review on the hotel listing page…" />
                    </Field>
                </div>
                <div className="column is-full">
                    <Field label="Then…">
                        <textarea className="textarea is-small" rows={2} value={form.hypothesisThen}
                                  onChange={e => set('hypothesisThen', e.target.value)}
                                  placeholder="e.g. …then booking conversion rate will increase by ~5%…" />
                    </Field>
                </div>
                <div className="column is-full">
                    <Field label="Because…">
                        <textarea className="textarea is-small" rows={2} value={form.hypothesisBecause}
                                  onChange={e => set('hypothesisBecause', e.target.value)}
                                  placeholder="e.g. …because users who see social proof during consideration have higher intent." />
                    </Field>
                </div>
            </div>
        </div>
    );
};

export default CreateExperimentForm;
